import { DOMAIN_WAVESEND_RU } from '~/src/helpers/constants';

import {
  DEFAULT_LANGUAGE,
  getLanguageDescriptor,
  isSupportedLanguage,
} from './languages';

// Same key the main app (app.pushwoosh.com) uses, so the choice stays consistent.
const STORAGE_KEY = 'PW_LANGUAGE';
// Shared across *.pushwoosh.com subdomains so app and SSO can read each other's choice.
const COOKIE_NAME = 'pw_language';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 1 year

// navigator.language sometimes returns legacy codes; map them to ours.
const NAVIGATOR_ALIASES: Record<string, string> = {
  iw: 'he', // legacy Hebrew
};

function readStoredLanguage(): string | undefined {
  try {
    return window.localStorage.getItem(STORAGE_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

function writeStoredLanguage(code: string): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, code);
  } catch {
    // private mode / storage disabled — cookie is still set below.
  }
}

function getCookieDomain(): string | undefined {
  const { hostname } = window.location;
  return hostname.endsWith('pushwoosh.com') ? '.pushwoosh.com' : undefined;
}

function readCookieLanguage(): string | undefined {
  const prefix = `${COOKIE_NAME}=`;
  const row = document.cookie.split('; ').find((part) => part.startsWith(prefix));
  return row ? decodeURIComponent(row.slice(prefix.length)) : undefined;
}

function writeCookieLanguage(code: string): void {
  const domain = getCookieDomain();
  const parts = [
    `${COOKIE_NAME}=${encodeURIComponent(code)}`,
    'path=/',
    `max-age=${COOKIE_MAX_AGE}`,
    'SameSite=Lax',
  ];
  if (domain) {
    parts.push(`domain=${domain}`);
  }
  document.cookie = parts.join('; ');
}

function normalizeNavigatorLanguage(): string | undefined {
  const candidates = navigator.languages && navigator.languages.length
    ? navigator.languages
    : [navigator.language];

  return candidates
    .map((raw) => raw?.split('-')[0]?.toLowerCase())
    .map((base) => (base ? NAVIGATOR_ALIASES[base] ?? base : undefined))
    .find((code): code is string => !!code && isSupportedLanguage(code));
}

// wavesend.ru serves a Russian-speaking audience, so ru is its baseline: it
// outranks the browser locale, but never a language the user picked explicitly.
function domainDefaultLanguage(): string | undefined {
  return window.location.origin.includes(DOMAIN_WAVESEND_RU) ? 'ru' : undefined;
}

// Precedence: explicit prior choice → cross-subdomain cookie → domain baseline
// → browser → default.
export function resolveInitialLanguage(): string {
  const stored = readStoredLanguage();
  if (stored && isSupportedLanguage(stored)) {
    return stored;
  }

  const cookie = readCookieLanguage();
  if (cookie && isSupportedLanguage(cookie)) {
    return cookie;
  }

  const domainDefault = domainDefaultLanguage();
  if (domainDefault) {
    return domainDefault;
  }

  return normalizeNavigatorLanguage() ?? DEFAULT_LANGUAGE;
}

export function persistLanguage(code: string): void {
  writeStoredLanguage(code);
  writeCookieLanguage(code);
}

// Keeps <html lang> / <html dir> in sync — dir matters for RTL languages (ar, he).
export function applyDocumentLanguage(code: string): void {
  const root = document.documentElement;
  root.lang = code;
  root.dir = getLanguageDescriptor(code)?.dir ?? 'ltr';
}
